/**
 * The settings page's building blocks (decision 77; part 11e, table SP; PLAN.md §3, step 1: cockatrice-ui's Section and
 * SettingsRow): a section under its heading, a setting's row (its label and help on the left, one control on the right),
 * and a row for a setting the design shows that isn't built yet. The ids stay the page's own (`<id>-label`,
 * `<id>-help`, `<id>-title`), which its controls and the tests point at.
 */
import Section from '../ui/Section.ts';
import SettingsRow from '../ui/SettingsRow.ts';
import { component, el } from '../ui/solid.ts';
import type { Child } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/**
 * A setting's row (SP1–SP18): its label (a `<label>` for the control `id`, or the name of a group the control is
 * labelled by) and its help on the left, one control on the right; `inline` keeps a checkbox on the right on phones.
 */
export function Row(id: string, label: string, help: string | null, control: Child, kind: 'label' | 'group' = 'label', inline = false): Child {
  return component(SettingsRow, {
    ids: id,
    for: kind === 'label' ? id : undefined,
    label,
    help: help ?? undefined,
    inline,
    get children() {
      return control;
    },
  });
}

/** SP6, SP12, SP14, SP17: a setting the design shows that isn't built yet: its name and help, "(Coming later)". */
export function Later(shell: Shell, label: string, help: string, control?: string): Child {
  return component(SettingsRow, {
    class: 'later-row',
    label,
    help,
    get children() {
      return el('span', { class: 'later' }, control === undefined ? shell.t('home.later') : shell.t('home.laterName', { name: control }));
    },
  });
}

/** A section of the page: its heading (focused when its entry in the list is pressed, SL3) and its rows. */
function SettingsSection(id: string, title: string, heading: (node: HTMLElement) => void, ...rows: readonly Child[]): Child {
  return component(Section, {
    class: 'set-section',
    title,
    headingId: `${id}-title`,
    headingFocusable: true,
    headingRef: heading,
    get children() {
      return rows;
    },
  });
}

export { SettingsSection as Section };
